import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/firebase.config';
import { createUserProfile } from '../../firebase/user.service';
import { useAuth } from '../../context/AuthContext';

const ROLE_OPTIONS = [
    'Real Estate Agent',
    'Broker',
    'Team Lead',
    'Loan Officer',
    'Property Manager',
    'Other',
];

const uploadProfilePhoto = (uid, file) => {
    return new Promise((resolve, reject) => {
        const storageRef = ref(storage, `profilePhotos/${uid}`);
        const task = uploadBytesResumable(storageRef, file);
        task.on(
            'state_changed',
            null,
            (err) => reject(err),
            async () => {
                const url = await getDownloadURL(task.snapshot.ref);
                resolve(url);
            }
        );
    });
};

/**
 * Detect auth provider from Firebase Auth providerData.
 * Maps 'google.com' -> 'google', everything else -> 'password'.
 */
const detectProvider = (user) => {
    if (!user?.providerData?.length) return 'password';
    const providerId = user.providerData[0].providerId;
    return providerId === 'google.com' ? 'google' : 'password';
};

const CompleteProfile = () => {
    const { user, refreshUserProfile } = useAuth();
    const navigate = useNavigate();

    const provider = detectProvider(user);
    const isGoogle = provider === 'google';

    const [fullName, setFullName] = useState(user?.displayName || '');
    const [phone, setPhone] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [role, setRole] = useState('');
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(isGoogle ? (user?.photoURL || null) : null);
    const [useGooglePhoto, setUseGooglePhoto] = useState(isGoogle && !!user?.photoURL);

    const [errors, setErrors] = useState({});
    const [globalError, setGlobalError] = useState('');
    const [loading, setLoading] = useState(false);

    const photoInputRef = useRef(null);

    useEffect(() => {
        console.info('[onboarding]', {
            event: 'profile_completion_started',
            uid: user?.uid || null,
            email: user?.email || null,
            provider,
            timestamp: new Date().toISOString(),
        });
    }, [user?.uid, user?.email, provider]);

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setErrors((prev) => ({ ...prev, profilePhoto: 'Please select an image file.' }));
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setErrors((prev) => ({ ...prev, profilePhoto: 'Image must be under 5 MB.' }));
            return;
        }
        setProfilePhoto(file);
        setUseGooglePhoto(false);
        setErrors((prev) => ({ ...prev, profilePhoto: '' }));
        const reader = new FileReader();
        reader.onload = (ev) => setPhotoPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const validate = () => {
        const e = {};
        if (!fullName.trim()) e.fullName = 'Full name is required.';
        if (!phone.trim()) e.phone = 'Phone number is required.';
        if (!companyName.trim()) e.companyName = 'Company name is required.';
        if (!role) e.role = 'Please select a role.';
        if (!profilePhoto && !useGooglePhoto) e.profilePhoto = 'Profile photo is required.';
        return e;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setGlobalError('');

        const fieldErrors = validate();
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            return;
        }
        setErrors({});
        setLoading(true);

        try {
            let photoURL = '';

            if (profilePhoto) {
                photoURL = await uploadProfilePhoto(user.uid, profilePhoto);
            } else if (useGooglePhoto && user.photoURL) {
                photoURL = user.photoURL;
            }

            await createUserProfile(user, {
                fullName: fullName.trim(),
                phone: phone.trim(),
                companyName: companyName.trim(),
                role,
                photoURL,
                provider,
            });

            console.info('[onboarding]', {
                event: 'profile_completion_success',
                uid: user.uid,
                email: user.email || null,
                provider,
                timestamp: new Date().toISOString(),
            });

            console.info('[onboarding]', {
                event: 'onboarding_completed',
                uid: user.uid,
                provider,
                timestamp: new Date().toISOString(),
            });

            localStorage.setItem('uid', user.uid);
            await refreshUserProfile();
            navigate('/dashboard', { replace: true });
        } catch (err) {
            console.error('Profile completion error:', err);
            setGlobalError(err.message || 'Failed to save profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-2 py-8">
            <form
                className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-8 md:p-10 flex flex-col gap-2"
                onSubmit={handleSubmit}
                noValidate
            >
                <h2 className="text-2xl md:text-3xl font-bold mb-1">Complete Your Profile</h2>
                <p className="text-gray-500 text-base mb-4">
                    Fill in your details to finish setting up your account
                </p>

                {/* Account info banner */}
                <div className="flex items-center gap-3 bg-blue-50 rounded-lg px-4 py-3 mb-2">
                    {isGoogle ? (
                        <svg width="20" height="20" viewBox="0 0 48 48">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                    ) : (
                        <span className="text-gray-500 text-lg">📧</span>
                    )}
                    <div>
                        <p className="text-sm font-medium text-gray-800">Signed in as</p>
                        <p className="text-sm text-gray-600">{user?.email}</p>
                    </div>
                </div>

                {/* Profile Photo */}
                <label className="text-sm font-medium">Profile Photo <span className="text-red-500">*</span></label>
                <div className="flex items-center gap-4 mt-1 mb-1">
                    <div
                        className="w-20 h-20 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-gray-500 transition relative"
                        onClick={() => photoInputRef.current?.click()}
                        title="Click to select photo"
                    >
                        {photoPreview ? (
                            <img
                                src={photoPreview}
                                alt="Preview"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="text-gray-400 text-3xl select-none">📷</span>
                        )}
                    </div>
                    <div>
                        <button
                            type="button"
                            onClick={() => photoInputRef.current?.click()}
                            className="text-sm text-black underline hover:text-gray-700"
                            disabled={loading}
                        >
                            {photoPreview ? 'Change Photo' : 'Upload Photo'}
                        </button>
                        <p className="text-xs text-gray-400 mt-0.5">
                            {useGooglePhoto ? 'Using your Google photo — click to change' : 'JPG, PNG — max 5 MB'}
                        </p>
                    </div>
                    <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoChange}
                        disabled={loading}
                    />
                </div>
                {errors.profilePhoto && (
                    <p className="text-red-500 text-xs -mt-1">{errors.profilePhoto}</p>
                )}

                {/* Full Name */}
                <label className="text-sm font-medium mt-3">
                    Full Name <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center bg-gray-100 rounded-md px-3 mt-1">
                    <span className="text-gray-400 mr-2">👤</span>
                    <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Your full name"
                        className="w-full bg-transparent outline-none py-3 text-base"
                        disabled={loading}
                    />
                </div>
                {errors.fullName && <p className="text-red-500 text-xs">{errors.fullName}</p>}

                {/* Phone Number */}
                <label className="text-sm font-medium mt-3">
                    Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center bg-gray-100 rounded-md px-3 mt-1">
                    <span className="text-gray-400 mr-2">📞</span>
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+1 (555) 000-0000"
                        className="w-full bg-transparent outline-none py-3 text-base"
                        disabled={loading}
                    />
                </div>
                {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}

                {/* Company Name */}
                <label className="text-sm font-medium mt-3">
                    Company Name <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center bg-gray-100 rounded-md px-3 mt-1">
                    <span className="text-gray-400 mr-2">🏢</span>
                    <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Your brokerage or company"
                        className="w-full bg-transparent outline-none py-3 text-base"
                        disabled={loading}
                    />
                </div>
                {errors.companyName && <p className="text-red-500 text-xs">{errors.companyName}</p>}

                {/* Role */}
                <label className="text-sm font-medium mt-3">
                    Role <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center bg-gray-100 rounded-md px-3 mt-1">
                    <span className="text-gray-400 mr-2">💼</span>
                    <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full bg-transparent outline-none py-3 text-base appearance-none"
                        disabled={loading}
                    >
                        <option value="">Select your role</option>
                        {ROLE_OPTIONS.map((r) => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                </div>
                {errors.role && <p className="text-red-500 text-xs">{errors.role}</p>}

                {/* Errors */}
                {globalError && (
                    <div className="text-red-500 text-sm text-center mt-2 bg-red-50 rounded p-2">
                        {globalError}
                    </div>
                )}

                {/* Submit */}
                <button
                    className="w-full bg-black text-white rounded-md py-3 font-semibold text-lg mt-4 hover:bg-gray-900 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    type="submit"
                    disabled={loading}
                >
                    {loading && (
                        <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v8z"
                            />
                        </svg>
                    )}
                    {loading ? 'Saving...' : 'Complete Setup'}
                </button>

                <div className="mt-4 text-xs text-gray-500 text-center">
                    By completing your profile, you agree to the{' '}
                    <a href="#" className="underline">Terms</a>,{' '}
                    <a href="#" className="underline">Privacy Policy</a>, and{' '}
                    <a href="#" className="underline">Refund &amp; Cancellation</a> policies.
                </div>
            </form>
        </div>
    );
};

export default CompleteProfile;
